import {
    MANUFACTURERBOD_PLACEHOLDERS,
    PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES,
    PART_TYPES,
} from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { Key } from 'react'
import type { ContentItemViewNodesType, DataNode } from '../types'

/**
 * Type guard to validate DataNode structure
 * @param {any} node - The object to validate
 * @returns {boolean} Whether the input is a valid DataNode
 * @description Ensures the object has required properties with correct types
 */
export const isValidDataNode = (node: any): node is DataNode => {
    return (
        node !== null &&
        typeof node === 'object' &&
        'key' in node &&
        typeof node.key === 'string' &&
        'contentItemNodes' in node &&
        Array.isArray(node.contentItemNodes) &&
        'type' in node &&
        Object.values(NODE_TYPES).includes(node.type) &&
        'order' in node &&
        typeof node.order === 'number'
    )
}

/** Get the label of the active tab */
export const getActiveTabLabel = (tabIndex: number) =>
    Object.values(PART_TYPES)[tabIndex - 1]

/**
 * Find and recursively updates the target tree node's children and toggle its expanded state
 * @param treeNodes - Array of DataNodes to traverse
 * @param targetNode - The key of the parent node to update
 * @param newChildren - Optional new children to replace existing children
 * @returns A new tree data structure with the specified node updated
 */
export const toggleExpandedRecursively = (
    treeNodes: DataNode[],
    targetNode: Key,
    newChildren: DataNode[] = []
): DataNode[] => {
    if (!Array.isArray(treeNodes) || !isValidDataNode(treeNodes[0])) {
        console.error('Invalid treeNode input. Expected an array of DataNodes.')
        return []
    }

    // deep clone to prevent mutation
    const oldTree = JSON.parse(JSON.stringify(treeNodes)) as DataNode[]

    const buildChildNodes = (nodes: DataNode[]): DataNode[] =>
        nodes.map((node) => {
            // if target node found, update its children and expanded state
            if (node.key === targetNode) {
                return {
                    ...node,
                    children: newChildren,
                    expanded: !node.expanded, //toggle expanded state
                }
            }
            // or, recursively update children if they exist
            if (node.children) {
                return {
                    ...node,
                    children: buildChildNodes(node.children),
                }
            }
            // else, return node unchanged if no match found
            return node
        })

    const updatedTree = buildChildNodes(oldTree)

    // check if target node was found by comparing the updated tree
    const wasNodeFound = JSON.stringify(updatedTree) !== JSON.stringify(oldTree)
    if (!wasNodeFound) {
        console.warn(`Target node '${String(targetNode)}' not found in tree`)
    }

    return updatedTree
}

/**
 * Finds node keys that have children recursively
 * @param nodes - Array of data nodes
 * @returns Array of keys with children
 */
export const findWithChild = (nodes: DataNode[]): Key[] => {
    const keys: Key[] = []
    const traverseChildren = (node: DataNode) => {
        if (node.children?.length) {
            keys.push(node.key)
            for (const child of node.children) {
                traverseChildren(child)
            }
        }
    }
    nodes.forEach(traverseChildren)
    return keys
}

/**
 * Get all keys of DataNodes recursively
 * @param nodes - Array of data nodes
 * @returns - Array of all keys
 */
export const getAllKeys = (nodes: DataNode[] = []): string[] => {
    let keys: string[] = []
    nodes.forEach((node) => {
        keys.push(node.key) // add the current node's key

        if (node.children) {
            keys = keys.concat(getAllKeys(node.children)) // add keys from children recursively
        }
    })
    return keys
}

/**
 * Merges the loading article IDs with the root article content item IDs
 * @param articlesLoading - Array of article IDs that are loading
 * @param rootArticleContentItemIds - Array of root article content item IDs
 * @returns - Array of unique article IDs that are loading
 */
export const mergeLoadingArticleIds = (
    articlesLoading: string[],
    rootArticleContentItemIds?: string[]
): string[] => {
    return Array.from(
        new Set([...articlesLoading, ...(rootArticleContentItemIds || [])])
    ).filter((key) => key !== 'default')
}

/**
 * Find articles that are not loaded in the tree given the following conditions:
 * - Article is not found in the Content Item Nodes
 * - Article's children are not found in the Content Item Nodes
 * @param contentItemNodeIds - Array of Content Item Node IDs
 * @param articleContentItemChildrenIds - Map of Article ID to its children IDs
 * @returns - Array of Article ID not loaded
 */
export const findArticlesNotLoaded = (
    contentItemNodeIds: string[],
    articleContentItemChildrenIds: Map<string, Key[]>
) => {
    const contentItemIdsSet = new Set(contentItemNodeIds)

    // find the articles that don't have their children loaded
    const notLoadedArticleIds: string[] = []
    articleContentItemChildrenIds.forEach((childrenIds, articleId) => {
        // If the article is not loaded, we must fetch it, no need to check the children
        const isArticleLoaded = articleContentItemChildrenIds.has(articleId)

        if (!isArticleLoaded) {
            notLoadedArticleIds.push(articleId)
            return
        }

        if (isArticleLoaded && childrenIds.length === 0) {
            notLoadedArticleIds.push(articleId)
        }

        // If article's children is not loaded, we must fetch it
        const isChildrenLoaded = childrenIds.every((childId) =>
            contentItemIdsSet.has(childId.toString())
        )
        if (!isChildrenLoaded) {
            notLoadedArticleIds.push(articleId)
        }
    })

    return notLoadedArticleIds
}

/**
 * Merges two arrays of content items by applying updates.
 * @param existingItems - The current array of content items.
 * @param updatedItems - The array of content items containing updates.
 * @param overwrite - When true, only include items that have an update; otherwise, merge updates with existing items.
 * @returns The merged array of content items.
 */
export const mergeContentItems = (
    existingItems: Partial<ContentItemViewNodesType>[],
    updatedItems: Partial<ContentItemViewNodesType>[],
    overwrite: boolean = false
): Partial<ContentItemViewNodesType>[] => {
    // create a lookup map of updated items keyed by id
    const updatesById = new Map(
        updatedItems.filter((item) => item.id).map((item) => [item.id, item])
    )
    // overwrite = false, retain all items (updating those that have a corresponding update).
    // overwrite = true, only retain items that have a corresponding update
    const mergedExistingItems = existingItems
        .filter((item) => (overwrite ? updatesById.has(item.id!) : true))
        .map((item) =>
            item.id && updatesById.has(item.id)
                ? updatesById.get(item.id)!
                : item
        )
    // find updated items that are new
    const existingIds = new Set(existingItems.map((item) => item.id))
    const additionalUpdatedItems = updatedItems.filter(
        (item) => item.id && !existingIds.has(item.id)
    )
    return [...mergedExistingItems, ...additionalUpdatedItems]
}

/**
 * Creates a debounced version of a function that delays its execution until after `timeout` milliseconds.
 * The returned function includes a `.cancel()` method to clear any pending timeout.
 *
 * @template T The type of the function to debounce.
 * @param {T} func The function to debounce.
 * @param {number} [timeout=100] The delay in milliseconds.
 * @returns {DebouncedFunction<T>} The debounced function with an attached `.cancel()` method.
 */
// Define a type for the debounced function with a cancel method property
type DebouncedFunction<T extends (...args: any[]) => void> = T & {
    cancel: () => void
}

export const withDebounce = <T extends (...args: any[]) => void>(
    func: T,
    timeout = 100
): DebouncedFunction<T> => {
    let timer: ReturnType<typeof setTimeout> | undefined

    // The core debounced function logic
    const debouncedFunc = ((...args: Parameters<T>) => {
        // Clear existing timer if function is called again before timeout
        if (timer) {
            clearTimeout(timer)
        }
        timer = setTimeout(() => {
            func(...args)
            timer = undefined // Reset timer after execution
        }, timeout)
    }) as DebouncedFunction<T> // Cast the function to include the .cancel method type

    // Attach the cancel method to the debounced function
    debouncedFunc.cancel = () => {
        // Clear the timeout if it exists
        if (timer) {
            clearTimeout(timer)
            timer = undefined
        }
    }

    return debouncedFunc
}

/**
 * Converts a string to PascalCase.
 *
 * @param input - The input string to convert to PascalCase.
 * @returns A string in PascalCase.
 */
export const toPascalCase = (input: string | undefined) => {
    if (!input) return ''

    return (
        input
            // replace underscores and hyphens with spaces
            .replace(/[_-]+/g, ' ')
            .split(' ')
            .filter((word) => word.length > 0)
            // Convert each word: first letter uppercase, rest lowercase
            .map(
                (word) =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join('')
    )
}

/**
 * Resolves an object's properties based on specific rules:
 * - Replaces `null` or `undefined` values with an empty string (`''`).
 * - Keeps `false` boolean values as `false`.
 * - Leaves all other values unchanged.
 *
 * @template T - The type of the object.
 * @param {T | Partial<T>} element - The object to resolve.
 * @returns {T} A new object with resolved property values.
 */
export const nullOrUndefinedResolver = <T extends Record<string, any>>(
    element: T | Partial<T>
): T => {
    if (!element) return {} as T

    return Object.entries(element).reduce((acc, [key, value]) => {
        let resolvedValue: any

        if (value === null || value === undefined) {
            resolvedValue = ''
        } else if (value === false) {
            resolvedValue = false
        } else {
            resolvedValue = value
        }

        return {
            ...acc,
            [key]: resolvedValue,
        }
    }, {} as T)
}

/**
 * Replaces an element in the target array with a new value.
 * Supports replacing a single object or an array of objects.
 *
 * @param target - The original array of elements.
 * @param targetId - The ID of the element to replace.
 * @param replacement - A single object or an array of objects to replace the element.
 * @returns A new array with the replacement applied.
 */
export const replaceInArray = <T extends Partial<{ id: string }>>(
    target: T[] = [],
    targetId: string,
    replacement: T | T[]
): T[] => {
    const index = target.findIndex((obj) => obj.id === targetId)

    if (index === -1) return target

    let newArray = [...target]

    if (Array.isArray(replacement)) {
        newArray.splice(index, 1, ...replacement)
    } else {
        newArray[index] = replacement
    }
    return newArray
}

/**
 * Checks if the node is a manufacturer or BOD content item
 * @param {DataNode} node - The node to check
 * @returns {boolean} True if the node is a manufacturer or BOD content item, false otherwise
 */
export const isManufacturerOrBODPill = (node: DataNode) => {
    const placeHolders = Object.values(MANUFACTURERBOD_PLACEHOLDERS) as string[]
    const firstElementText = node.contentItemNodes?.[0]?.text || ''
    // special condition for keyed manufacturers
    const isKeyedManufacturer = placeHolders.includes(firstElementText)

    return (node.isManufacturers || node.isBOD) && !isKeyedManufacturer
}

/**
 * Checks if the given element is an editable field (input, textarea, or contentEditable).
 * @param element The target HTMLElement
 * @returns true if the element is editable, false otherwise
 */
export const isEditableElement = (element: HTMLElement): boolean => {
    const tag = element.tagName
    return tag === 'INPUT' || tag === 'TEXTAREA' || element.isContentEditable
}
